import React, { useEffect, useState } from 'react';
import { Routes, Route, BrowserRouter, Navigate, useLocation } from 'react-router-dom';
import { Amplify, Auth, Hub } from 'aws-amplify';
import Loading from './Loading';
import Posts from './Posts';
import Navbar from './Navbar';
import ImgDropAndCrop from './ImgDropAndCrop';
import Login from './Login';
import Footer from './Footer';
import Shared from './Shared';
import CookieConsentBanner from './CookieConsentBanner';
import LegalNotice from './LegalNotice';
import PrivacyPolicy from './PrivacyPolicy';
import CookiePolicy from './CookiePolicy';
import TermsOfUse from './TermsOfUse';
import AffiliateDisclosurePage from './AffiliateDisclosurePage';
import SetupCategoryPage from './SetupCategoryPage';
import SetupPage from './SetupPage';
import LatestPage from './LatestPage';
import SearchHubPage from './SearchHubPage';
import AuthoredPage from './AuthoredPage';
import { usePageSeo } from './seo';
import { usePageGroupTracking } from './pageAnalytics';
import { sendPageViewIfConsented, trackEventIfConsented } from './analyticsConsent';
import { fetchRecentPosts } from './postData';
import config from './aws-exports';
import './style.css';

Amplify.configure(config);

const AnalyticsPageTracker = () => {
  const location = useLocation();

  useEffect(() => {
    sendPageViewIfConsented(`${location.pathname}${location.search}${location.hash}`);
  }, [location]);

  return null;
};

const HomePage = ({ children }) => {
  usePageSeo({
    title: 'Desk setup ideas, real setups and tagged gear discovery',
    description:
      'Explore real desk setups, open search-ready setup idea hubs, discover tagged gear, and subscribe for curated workspace inspiration from 101Setups.',
    canonicalPath: '/',
  });
  usePageGroupTracking('home', {
    page_role: 'homepage',
  });

  return children;
};

const UploadPage = () => {
  usePageSeo({
    title: 'Upload your desk setup',
    description: 'Upload a desk setup to the 101Setups community.',
    canonicalPath: '/ImgDropAndCrop',
    robots: 'noindex,nofollow',
  });
  usePageGroupTracking('upload_page', {
    page_role: 'upload',
  });

  return <ImgDropAndCrop />;
};

const LoginPage = () => {
  usePageSeo({
    title: 'Share your desk setup',
    description: 'Create an account, sign in, and share your desk setup with the 101Setups community.',
    canonicalPath: '/login',
    robots: 'noindex,nofollow',
  });
  usePageGroupTracking('auth_page', {
    page_role: 'login',
  });

  return <Login />;
};

const LegalPage = ({ title, description, canonicalPath, pageRole, children }) => {
  usePageSeo({
    title,
    description,
    canonicalPath,
  });
  usePageGroupTracking('legal_page', {
    page_role: pageRole,
  });

  return children;
};

function App() {
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [logged, setLogged] = useState(false);
  const [userName, setUserName] = useState('');

  useEffect(() => {
    let isMounted = true;

    const loadHomePosts = async () => {
      const recentPosts = await fetchRecentPosts(8);

      if (!isMounted) {
        return;
      }

      setPosts(recentPosts);
      setLoading(false);
    };

    const unsubscribe = Hub.listen('datastore', async hubData => {
      const { event } = hubData.payload;

      if (event === 'ready') {
        const readyPosts = await fetchRecentPosts(8);

        if (!isMounted) {
          return;
        }

        setPosts(readyPosts);
        setLoading(false);
      }
    });

    loadHomePosts();

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    const checkUser = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        setLogged(true);
        setUserName(user.username);
      } catch (error) {
        setLogged(false);
        setUserName('');
      }
    };

    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      const authEvent = payload?.event;
      const authUser = payload?.data;

      if (authEvent === 'signedIn') {
        setLogged(true);
        setUserName(authUser?.username || '');
        trackEventIfConsented('login', {
          method: 'amplify_authenticator',
        });
      }

      if (authEvent === 'signUp' || authEvent === 'autoSignIn') {
        trackEventIfConsented('sign_up', {
          method: 'amplify_authenticator',
        });
      }

      if (authEvent === 'signedOut') {
        setLogged(false);
        setUserName('');
      }
    });

    checkUser();

    return unsubscribe;
  }, []);

  if (loading) {
    return (
      <main>
        <Loading />
      </main>
    );
  }

  const appRoutes = (
    <Routes>
      <Route
        path="/"
        element={
          <HomePage>
            <Posts posts={posts} />
          </HomePage>
        }
      />
      <Route path="/setup-ideas" element={<Navigate to="/desk-setup-ideas" replace />} />
      <Route path="/desk-setup-ideas" element={<SearchHubPage hubSlug="desk-setup-ideas" />} />
      <Route path="/minimalist-desk-setup" element={<SearchHubPage hubSlug="minimalist-desk-setup" />} />
      <Route path="/home-office-desk-setup" element={<SearchHubPage hubSlug="home-office-desk-setup" />} />
      <Route path="/desk-lighting-ideas" element={<SearchHubPage hubSlug="desk-lighting-ideas" />} />
      <Route path="/monitor-arm-desk-setup" element={<SearchHubPage hubSlug="monitor-arm-desk-setup" />} />
      <Route path="/how-to-hide-cables-on-a-desk" element={<SearchHubPage hubSlug="how-to-hide-cables-on-a-desk" />} />
      <Route path="/setup-ideas/:categorySlug" element={<SetupCategoryPage />} />
      <Route path="/collections/:collectionSlug" element={<AuthoredPage pageType="collection" />} />
      <Route path="/gear/:gearSlug" element={<AuthoredPage pageType="gear" />} />
      <Route path="/setup/:slug" element={<SetupPage />} />
      <Route path="/latest" element={<LatestPage />} />
      <Route path="/ImgDropAndCrop" element={<UploadPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/shared/:id" element={<Shared />} />
      <Route
        path="/legal"
        element={
          <LegalPage
            title="Legal notice"
            description="Read the 101Setups legal notice, operator details, and contact information."
            canonicalPath="/legal"
            pageRole="legal_notice"
          >
            <LegalNotice />
          </LegalPage>
        }
      />
      <Route
        path="/privacy"
        element={
          <LegalPage
            title="Privacy policy"
            description="Read how 101Setups handles personal data, analytics consent, newsletter subscriptions, and account information."
            canonicalPath="/privacy"
            pageRole="privacy"
          >
            <PrivacyPolicy />
          </LegalPage>
        }
      />
      <Route
        path="/cookies"
        element={
          <LegalPage
            title="Cookie policy"
            description="Read the 101Setups cookie policy and analytics consent information."
            canonicalPath="/cookies"
            pageRole="cookies"
          >
            <CookiePolicy />
          </LegalPage>
        }
      />
      <Route
        path="/terms"
        element={
          <LegalPage
            title="Terms of use"
            description="Read the 101Setups terms of use for accounts, content, and site access."
            canonicalPath="/terms"
            pageRole="terms"
          >
            <TermsOfUse />
          </LegalPage>
        }
      />
      <Route
        path="/affiliate-disclosure"
        element={
          <LegalPage
            title="Affiliate disclosure"
            description="Read how affiliate-supported links work on 101Setups and how Amazon store routing is handled."
            canonicalPath="/affiliate-disclosure"
            pageRole="affiliate_disclosure"
          >
            <AffiliateDisclosurePage />
          </LegalPage>
        }
      />
    </Routes>
  );

  return (
    <BrowserRouter>
      <AnalyticsPageTracker />
      <Navbar logged={logged} userName={userName} />
      {appRoutes}
      <Footer />
      <CookieConsentBanner />
    </BrowserRouter>
  );
}

export default App;
